import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import Modal from '@components/common/Modal/Modal';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';
import applicationsService from '@/services/applications/applicationsService';

interface CohortStudentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    cohortId: string;
    cohortName: string;
}

const CohortStudentsModal = ({ isOpen, onClose, cohortId, cohortName }: CohortStudentsModalProps) => {
    const { data: listResponse, isLoading } = useQuery({
        queryKey: ['cohort-students', cohortId],
        queryFn: () =>
            applicationsService.adminList(
                1,
                100, // Fetch up to 100 students for export
                undefined,
                cohortId,
                'ASSIGNED_TO_COHORT',
            ),
        enabled: isOpen,
    });

    const students = useMemo(() => listResponse?.data?.data ?? [], [listResponse]);

    const handleDownloadExcel = () => {
        if (students.length === 0) return;

        const dataToExport = students.map((s: any) => ({
            'Student Name': s.full_name,
            'Email Address': s.email,
            'Enrollment Number': s.enrollment_number || 'N/A',
            'Payment Status': s.payment_status || 'PENDING',
            'Email Sent': s.email_sent_status ? 'Yes' : 'No',
            'Registration Date': new Date(s.created_at).toLocaleDateString(),
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Assigned Students');

        // Autofit columns
        const maxLens = Object.keys(dataToExport[0] || {}).map(key => {
            return Math.max(key.length, ...dataToExport.map((row: any) => String(row[key] || '').length));
        });
        worksheet['!cols'] = maxLens.map(len => ({ wch: len + 3 }));

        XLSX.writeFile(workbook, `${cohortName.replace(/\s+/g, '_')}_Student_List.xlsx`);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Students in ${cohortName}`}
            maxWidth="max-w-[800px]"
        >
            <div className="flex flex-col gap-4 text-sm text-[var(--secondary-color)] p-4">
                {/* Header actions */}
                <div className="flex justify-between items-center bg-[var(--primary-surface-color)] p-3 rounded-xl border">
                    <div>
                        <span className="font-semibold block">Total Enrolled: {students.length} students</span>
                        <span className="text-xs text-[var(--disabled-text-color)]">Roster list including assignment dates and fee statuses.</span>
                    </div>
                    <Button
                        onClick={handleDownloadExcel}
                        disabled={students.length === 0}
                    >
                        <Icon name="download" size={14} className="mr-1 mt-[-2px] brightness-0 invert" />
                        Download Student List
                    </Button>
                </div>

                {/* Table list */}
                <div className="overflow-x-auto border rounded-xl max-h-[350px] custom-scroll">
                    <table className="w-full text-left border-collapse text-xs md:text-sm">
                        <thead>
                            <tr className="bg-[var(--primary-surface-color)] border-b text-[var(--disabled-text-color)] uppercase text-xs font-semibold">
                                <th className="p-3">Student Name</th>
                                <th className="p-3">Email</th>
                                <th className="p-3">Enrollment No.</th>
                                <th className="p-3">Payment Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="p-6 text-center text-[var(--disabled-text-color)]">
                                        Loading students list...
                                    </td>
                                </tr>
                            ) : students.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="p-6 text-center text-[var(--disabled-text-color)]">
                                        No students assigned to this cohort yet.
                                    </td>
                                </tr>
                            ) : (
                                students.map((s: any) => (
                                    <tr key={s.id} className="border-b hover:bg-[var(--primary-surface-color)]">
                                        <td className="p-3 font-semibold">{s.full_name}</td>
                                        <td className="p-3">{s.email}</td>
                                        <td className="p-3 font-mono font-medium text-blue-600">{s.enrollment_number || 'N/A'}</td>
                                        <td className="p-3">
                                            <span className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
                                                s.payment_status === 'PAID'
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                            }`}>
                                                {s.payment_status || 'PENDING'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-3 mt-2">
                    <button
                        type="button"
                        className="px-4 py-2 border rounded-lg hover:bg-slate-50 text-slate-700 font-semibold cursor-pointer text-sm"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CohortStudentsModal;
